import { useUsers, useUsersCount } from '@/api/user/user.api';
import { EmptyList } from '@/components/@next/Empty/EmptyList';
import { Pagination } from '@/components/@next/Pagination/Pagination';
import StandaloneLoader from '@/components/@next/StandaloneLoader';
import routes from '@/routes/routes';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import FiltersDialog, { UsersFilterDialogParams } from './FiltersDialog';
import ShowcaseCard from './ShowcaseCard';

const perPage = 9;

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '30px',
};

const MostPopularShowcases = ({ search }: { search: string | undefined }) => {
  const router = useRouter();
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSearch = !!search;

  const [filters, setFilters] = useState<UsersFilterDialogParams>({
    sameCity: false,
    secureMembers: false,
  });
  const { data: showcases = [], isLoading: isShowcasesLoading } = useUsers({
    orderBy: 'rating',
    limit: perPage,
    sameCity: filters.sameCity,
    secureMembers: filters.secureMembers,
    offset,
    search,
  });

  const { data: count, isLoading: isCountLoading } = useUsersCount({
    orderBy: 'rating',
    sameCity: filters.sameCity,
    secureMembers: filters.secureMembers,
    search,
  });

  const isFiltered = useMemo(() => {
    return Object.values(filters).some((i) => !!i);
  }, [filters]);

  useEffect(() => {
    return () => setOffset(0);
  }, [filters]);

  const current = Math.floor(offset / perPage) + 1;

  const isLoading = isShowcasesLoading || isCountLoading;

  return (
    <div
      ref={containerRef}
      className={cn('pt-8 pb-12 px-2.5 md:px-8 lg:px-24 text-left', !isSearch && 'mt-20 mb-16')}
    >
      <div className="flex justify-center items-center relative">
        <h2 className="text-2xl font-extrabold">
          {isSearch ? 'Showcase search result' : 'Most popular showcases'}
        </h2>

        <div className="absolute right-0 -top-[calc(100%+1.5rem)] sm:top-auto">
          {isFiltered && (
            <span className="w-4 h-4 bg-danger rounded-full absolute -top-1 -right-1" />
          )}
          <FiltersDialog onApplyClick={setFilters} filters={filters} />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center my-10">
          <StandaloneLoader />
        </div>
      )}

      {!!showcases.length && !isLoading && (
        <div style={gridStyle} className="mt-16">
          {showcases.map((showcase) => (
            <ShowcaseCard
              key={showcase.id}
              showcase={showcase}
              onClick={() => router.push(routes.showcase({ userId: showcase.id }))}
            />
          ))}
        </div>
      )}

      {!showcases.length && !isLoading && <EmptyList text="No showcases found" />}

      {!!count && !isLoading && (
        <div className="flex justify-center">
          <Pagination
            current={current}
            pageSize={perPage}
            total={count}
            className="mt-12"
            onChange={(pageNumber) => {
              setOffset(Math.max(0, pageNumber - 1) * perPage);
            }}
            onPageChangeScrollTo={containerRef}
          />
        </div>
      )}
    </div>
  );
};

export default MostPopularShowcases;
